import React, { useEffect, useState } from 'react';
import {
    AspectRatio,
    Box,
    Button,
    Card,
    CardContent,
    CardOverflow,
    CircularProgress,
    DialogContent,
    DialogTitle,
    FormControl,
    FormLabel,
    Grid,
    IconButton,
    Input,
    Modal,
    ModalClose,
    ModalDialog,
    Stack,
    Textarea,
    Typography,
} from '@mui/joy';
import { Add, Delete, Edit } from '@mui/icons-material';
import { PageStandard } from '../utils/PageStandard';
import { StickerModel } from '../utils/Model';
import { createSticker, deleteSticker, getStickers, updateSticker } from '../utils/ViewModel';

interface FormState {
    title: string;
    description: string;
    img_url: string;
}

const emptyForm: FormState = { title: '', description: '', img_url: '' };

export function Stickers() {
    const [stickers, setStickers] = useState<StickerModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // null = closed, undefined = create, StickerModel = edit
    const [modal, setModal] = useState<StickerModel | null | undefined>(null);
    const [form, setForm] = useState<FormState>(emptyForm);

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        setStickers(await getStickers());
        setLoading(false);
    }

    function openCreate() {
        setForm(emptyForm);
        setModal(undefined);
    }

    function openEdit(s: StickerModel) {
        setForm({ title: s.title, description: s.description, img_url: s.img_url });
        setModal(s);
    }

    function closeModal() { setModal(null); }

    async function handleSave() {
        if (!form.title.trim() || !form.img_url.trim()) {
            alert('Titolo e URL immagine obbligatori');
            return;
        }
        setSaving(true);
        let ok: boolean;
        if (modal === undefined) {
            ok = await createSticker(form.title.trim(), form.description.trim(), form.img_url.trim());
        } else {
            ok = await updateSticker(modal!.id, form.title.trim(), form.description.trim(), form.img_url.trim());
        }
        setSaving(false);
        if (ok) { closeModal(); await load(); }
    }

    async function handleDelete(id: string) {
        if (!window.confirm('Eliminare lo sticker? Verranno rimossi anche tutti gli assignment.')) return;
        if (await deleteSticker(id)) await load();
    }

    const isCreate = modal === undefined;

    return (
        <PageStandard
            title="Stickers"
            sidebarVisible
            headerEnd={
                <Button startDecorator={<Add />} onClick={openCreate}>
                    Nuovo sticker
                </Button>
            }
        >
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                    <CircularProgress />
                </Box>
            ) : stickers.length === 0 ? (
                <Typography level="body-md" color="neutral" sx={{ mt: 4, textAlign: 'center' }}>
                    Nessuno sticker. Creane uno con il pulsante in alto.
                </Typography>
            ) : (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    {stickers.map(s => (
                        <Grid key={s.id} xs={12} sm={6} md={4} lg={3}>
                            <Card variant="outlined" sx={{ height: '100%' }}>
                                <CardOverflow>
                                    <AspectRatio ratio="1">
                                        <img
                                            src={s.img_url}
                                            alt={s.title}
                                            style={{ objectFit: 'contain', background: '#f5f5f5' }}
                                        />
                                    </AspectRatio>
                                </CardOverflow>
                                <CardContent>
                                    <Typography level="title-sm">{s.title}</Typography>
                                    <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                        {s.description || '—'}
                                    </Typography>
                                </CardContent>
                                <CardContent orientation="horizontal" sx={{ gap: 1 }}>
                                    <IconButton size="sm" variant="soft" onClick={() => openEdit(s)}>
                                        <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton size="sm" variant="soft" color="danger" onClick={() => handleDelete(s.id)}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Create / Edit modal */}
            <Modal open={modal !== null} onClose={closeModal}>
                <ModalDialog minWidth={420}>
                    <ModalClose />
                    <DialogTitle>{isCreate ? 'Nuovo sticker' : 'Modifica sticker'}</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <FormControl required>
                                <FormLabel>Titolo</FormLabel>
                                <Input
                                    value={form.title}
                                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                                    placeholder="es. La Mole"
                                />
                            </FormControl>
                            <FormControl required>
                                <FormLabel>URL immagine</FormLabel>
                                <Input
                                    value={form.img_url}
                                    onChange={e => setForm(f => ({ ...f, img_url: e.target.value }))}
                                    placeholder="https://api.gocicero.it/media/getFile/sticker.png"
                                />
                            </FormControl>
                            {form.img_url && (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <img
                                        src={form.img_url}
                                        alt="preview"
                                        style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }}
                                    />
                                </Box>
                            )}
                            <FormControl>
                                <FormLabel>Descrizione</FormLabel>
                                <Textarea
                                    minRows={2}
                                    value={form.description}
                                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                    placeholder="Descrizione opzionale"
                                />
                            </FormControl>
                            <Button loading={saving} onClick={handleSave}>
                                {isCreate ? 'Crea' : 'Salva'}
                            </Button>
                        </Stack>
                    </DialogContent>
                </ModalDialog>
            </Modal>
        </PageStandard>
    );
}
